import { useState } from 'react';
import type { Editor } from '@tiptap/react';
import { FiImage } from 'react-icons/fi';
import { buttonStyles } from '@/shared/ui/button';
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from '@/shared/ui/popover';
import type { ImageSlotInsert, RichTextEditorProps } from '@/shared/ui/rich-text-editor/types';
import { toolbarIconClass } from './toolbar-controls';

type ImageInvokerProps = {
  editor: Editor;
  isDisabled: boolean;
  renderImageSource: NonNullable<RichTextEditorProps['renderImageSource']>;
};

/**
 * The image control and the host's source picker inside it.
 *
 * The shared component is module-blind: it never reaches for the media module
 * itself. The host passes a renderer that receives `insert` and returns its own
 * picker UI. Without that slot the toolbar does not render the control at all —
 * a button that cannot reach an outcome is not a control.
 */
export function ImageInvoker({ editor, isDisabled, renderImageSource }: ImageInvokerProps) {
  const [open, setOpen] = useState(false);

  const insert: ImageSlotInsert = (src, alt) => {
    editor.chain().focus().setImage({ src, alt }).run();
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label="Image"
        disabled={isDisabled}
        className={buttonStyles({ variant: 'ghost', size: 'xs' })}
      >
        <FiImage className={toolbarIconClass} aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent align="start">
        <PopoverTitle>Image</PopoverTitle>
        {renderImageSource(insert)}
      </PopoverContent>
    </Popover>
  );
}
